import React, { useEffect, useMemo, useState } from 'react';
import {
  SenderIdDto,
  SenderIdAllocationDto,
  getSenderIds,
  createSenderId,
  updateSenderId,
  deleteSenderId,
  getAllocations,
  allocateSenderId,
  revokeSenderId
} from '../../services/SenderIdService';
import { getUsers, ApiUser } from '../../services/UserService';

const SenderIdManagement: React.FC = () => {
  const [senderIds, setSenderIds] = useState<SenderIdDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [q, setQ] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');

  const [createOpen, setCreateOpen] = useState(false);
  const [newSenderId, setNewSenderId] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [creating, setCreating] = useState(false);

  const [allocOpen, setAllocOpen] = useState(false);
  const [allocSender, setAllocSender] = useState<SenderIdDto | null>(null);
  const [users, setUsers] = useState<ApiUser[]>([]);
  const [userLoading, setUserLoading] = useState(false);
  const [allocUserId, setAllocUserId] = useState('');
  const [allocating, setAllocating] = useState(false);

  const [viewAllocOpen, setViewAllocOpen] = useState(false);
  const [viewAllocSender, setViewAllocSender] = useState<SenderIdDto | null>(null);
  const [allocations, setAllocations] = useState<SenderIdAllocationDto[]>([]);
  const [allocationsLoading, setAllocationsLoading] = useState(false);

  const filtered = useMemo(() => {
    return senderIds.filter(s => {
      const m1 = !statusFilter || s.status === statusFilter;
      const m2 = !q || s.senderId.toLowerCase().includes(q.toLowerCase());
      return m1 && m2;
    });
  }, [senderIds, statusFilter, q]);

  const loadSenderIds = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await getSenderIds({ status: statusFilter || undefined, q: q || undefined });
      setSenderIds(data);
    } catch (e: any) {
      setError(e.message || 'Failed to load sender IDs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadSenderIds(); /* eslint-disable-next-line */ }, []);

  const openCreate = () => {
    setNewSenderId('');
    setNewDescription('');
    setCreateOpen(true);
  };

  const submitCreate = async () => {
    if (!newSenderId.trim()) return;
    setCreating(true);
    try {
      const created = await createSenderId({ senderId: newSenderId.trim(), description: newDescription.trim() || undefined });
      setSenderIds(prev => [created, ...prev]);
      setCreateOpen(false);
    } catch (e: any) {
      alert(e.message || 'Failed to create sender ID');
    } finally {
      setCreating(false);
    }
  };

  const changeStatus = async (id: string, status: SenderIdDto['status']) => {
    try {
      const updated = await updateSenderId(id, { status });
      setSenderIds(prev => prev.map(s => s.id === id ? updated : s));
    } catch (e: any) {
      alert(e.message || 'Failed to update status');
    }
  };

  const removeSender = async (id: string) => {
    if (!window.confirm('Delete this Sender ID? This will remove all allocations.')) return;
    try {
      await deleteSenderId(id);
      setSenderIds(prev => prev.filter(s => s.id !== id));
    } catch (e: any) {
      alert(e.message || 'Failed to delete sender ID');
    }
  };

  const openAllocate = async (sender: SenderIdDto) => {
    setAllocSender(sender);
    setAllocUserId('');
    setAllocOpen(true);
    if (!users.length) {
      setUserLoading(true);
      try {
        const u = await getUsers();
        setUsers(u);
      } catch (e) {
        // ignore
      } finally {
        setUserLoading(false);
      }
    }
  };

  const submitAllocate = async () => {
    if (!allocSender || !allocUserId) return;
    setAllocating(true);
    try {
      await allocateSenderId(allocSender.id, allocUserId);
      setAllocOpen(false);
    } catch (e: any) {
      alert(e.message || 'Failed to allocate');
    } finally {
      setAllocating(false);
    }
  };

  const openViewAllocations = async (sender: SenderIdDto) => {
    setViewAllocSender(sender);
    setViewAllocOpen(true);
    setAllocationsLoading(true);
    try {
      const rows = await getAllocations(sender.id);
      setAllocations(rows);
    } catch (e: any) {
      alert(e.message || 'Failed to fetch allocations');
    } finally {
      setAllocationsLoading(false);
    }
  };

  const revoke = async (senderIdId: string, userId: string) => {
    if (!window.confirm('Remove this allocation from the user?')) return;
    try {
      await revokeSenderId(senderIdId, userId);
      setAllocations(prev => prev.filter(a => a.userId !== userId));
    } catch (e: any) {
      alert(e.message || 'Failed to remove allocation');
    }
  };

  return (
    <div className="p-6">
      <h1 className="text-2xl font-medium mb-6">Sender ID Management</h1>

      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-medium">Sender IDs</h2>
          <div className="flex items-center gap-2">
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search..." className="border rounded-md px-3 py-1.5 text-sm" />
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="border rounded-md px-3 py-1.5 text-sm">
              <option value="">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="active">Active</option>
              <option value="suspended">Suspended</option>
              <option value="rejected">Rejected</option>
            </select>
            <button onClick={loadSenderIds} className="px-3 py-1.5 text-sm border rounded-md">Refresh</button>
            <button onClick={openCreate} className="bg-[#FF5722] text-white px-4 py-2 rounded-md hover:bg-[#FF5722]/90">Create Sender ID</button>
          </div>
        </div>

        {loading && <div className="text-sm text-gray-500">Loading...</div>}
        {!loading && error && <div className="text-sm text-red-600">{error}</div>}

        {!loading && !error && (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sender ID</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filtered.map(s => (
                  <tr key={s.id}>
                    <td className="px-6 py-4 whitespace-nowrap">{s.senderId}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <select value={s.status} onChange={(e) => changeStatus(s.id, e.target.value as SenderIdDto['status'])} className="border rounded-md px-2 py-1 text-sm">
                        {['pending','approved','active','suspended','rejected'].map(x => <option key={x} value={x}>{x}</option>)}
                      </select>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{s.description || '-'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <button onClick={() => openAllocate(s)} className="text-blue-600 hover:text-blue-800 mr-3">Allocate</button>
                      <button onClick={() => openViewAllocations(s)} className="text-amber-600 hover:text-amber-800 mr-3">View Allocations</button>
                      <button onClick={() => removeSender(s.id)} className="text-red-600 hover:text-red-800">Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {createOpen && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-md w-full max-w-md p-6">
            <h3 className="text-lg font-medium mb-4">Create Sender ID</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Sender ID</label>
                <input value={newSenderId} onChange={(e) => setNewSenderId(e.target.value)} placeholder="e.g. MYBRAND" className="w-full border border-gray-300 rounded-md px-3 py-2" />
                <p className="mt-1 text-xs text-gray-500">3-11 alphanumeric characters.</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Description (optional)</label>
                <input value={newDescription} onChange={(e) => setNewDescription(e.target.value)} className="w-full border border-gray-300 rounded-md px-3 py-2" />
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button onClick={() => setCreateOpen(false)} className="px-4 py-2 border border-gray-300 rounded-md text-gray-600 hover:bg-gray-50">Cancel</button>
              <button onClick={submitCreate} disabled={creating} className="bg-[#FF5722] text-white px-4 py-2 rounded-md hover:bg-[#FF5722]/90 disabled:opacity-50">{creating ? 'Creating...' : 'Create'}</button>
            </div>
          </div>
        </div>
      )}

      {allocOpen && allocSender && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-md w-full max-w-md p-6">
            <h3 className="text-lg font-medium mb-4">Allocate Sender ID: {allocSender.senderId}</h3>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Select User</label>
              <select value={allocUserId} onChange={(e) => setAllocUserId(e.target.value)} className="w-full border border-gray-300 rounded-md px-3 py-2">
                <option value="">Select a user</option>
                {users.map(u => (
                  <option key={u.id} value={u.id}>{u.firstName} {u.lastName} - {u.companyEmail}</option>
                ))}
              </select>
              {userLoading && <div className="text-xs text-gray-500 mt-2">Loading users...</div>}
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button onClick={() => setAllocOpen(false)} className="px-4 py-2 border border-gray-300 rounded-md text-gray-600 hover:bg-gray-50">Cancel</button>
              <button onClick={submitAllocate} disabled={allocating || !allocUserId} className="bg-[#FF5722] text-white px-4 py-2 rounded-md hover:bg-[#FF5722]/90 disabled:opacity-50">{allocating ? 'Allocating...' : 'Allocate'}</button>
            </div>
          </div>
        </div>
      )}

      {viewAllocOpen && viewAllocSender && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-md w-full max-w-2xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium">Allocations for {viewAllocSender.senderId}</h3>
              <button onClick={() => setViewAllocOpen(false)} className="px-3 py-1.5 border rounded-md">Close</button>
            </div>
            {allocationsLoading && <div className="text-sm text-gray-500">Loading...</div>}
            {!allocationsLoading && (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {allocations.map(a => (
                      <tr key={a.id}>
                        <td className="px-6 py-4 whitespace-nowrap">{a.userName || a.userId}</td>
                        <td className="px-6 py-4 whitespace-nowrap">{a.userEmail || '-'}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">{a.status}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          <button onClick={() => revoke(viewAllocSender.id, a.userId)} className="text-red-600 hover:text-red-800">Remove</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SenderIdManagement;


